import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import { getTranslatedName } from "../../../../../app/src/Utils/utils";

// Modal "Filtros" (contrato §2.2/§2.3, pantallas 04/13, 17, 17-B). Selección en borrador local:
// solo se confirma al estado de búsqueda (URL) al pulsar "Aplicar filtros" (onApply).
// orWord viene de t("search.buscar.or"): no puede vivir fuera del componente porque t() es un
// hook.
const joinWithOr = (labels, orWord) => {
  if (labels.length <= 1) return labels.join("");
  if (labels.length === 2) return `${labels[0]} ${orWord} ${labels[1]}`;
  return `${labels.slice(0, -1).join(", ")} ${orWord} ${labels[labels.length - 1]}`;
};

// `allNodeId` (OTT-2011, contrato de la Agenda §18.2): id del nodo "Todos los deportes", el estado
// sin filtro. Está marcado cuando no hay nada más marcado, marcarlo desmarca el resto y marcar
// cualquier otro lo desmarca; nunca se devuelve en onApply. Sin la prop (el buscador), no aplica.
const FilterModal = ({ open, onClose, roots, childrenByParent, loadChildren, selectedIds, onApply, dateLabel, onClearDate, allNodeId = null }) => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(() => new Set(selectedIds));
  const [expanded, setExpanded] = useState(() => new Set());
  // Borrador del check de fecha (mismo patrón que `draft` para los nodos): se puede desmarcar
  // dentro de la modal, pero solo se borra el filtro de verdad (onClearDate) al pulsar "Aplicar
  // filtros" — hasta entonces es reversible, como el resto de la selección.
  const [dateChecked, setDateChecked] = useState(true);
  // Foto de los filtros al ABRIR la modal (nodos + si había fecha): sirve para saber si el usuario
  // ha cambiado algo respecto a lo que ya estaba aplicado y decidir qué botón resaltar.
  const initialRef = useRef({ ids: new Set(), hadDate: false });
  const panelRef = useRef(null);
  const triggerFocusRef = useRef(null);

  const sortedRoots = useMemo(() => [...(roots || [])].sort((a, b) => a.order - b.order), [roots]);

  useEffect(() => {
    // Se siembra el borrador solo al ABRIR (no en cada cambio de selectedIds mientras está
    // abierto): el modal trabaja sobre una copia local hasta "Aplicar filtros".
    if (open) {
      triggerFocusRef.current = document.activeElement;
      initialRef.current = { ids: new Set(selectedIds), hadDate: !!dateLabel };
      setDraft(new Set(selectedIds));
      setDateChecked(true);
    } else if (triggerFocusRef.current) {
      triggerFocusRef.current.focus?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Bloquea el scroll de la página mientras la modal está abierta (mismo patrón que
  // ProductsComponent.js: guardar el overflow previo y restaurarlo al cerrar/desmontar).
  useEffect(() => {
    if (!open) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        onClose();
        return;
      }
      if (e.key === "Tab" && panelRef.current) {
        const focusable = panelRef.current.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        );
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    panelRef.current?.focus();
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  const toggleExpand = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
        loadChildren(id);
      }
      return next;
    });
  };

  const isAllNode = (id) => allNodeId != null && id === allNodeId;

  const toggle = (id) => {
    if (isAllNode(id)) {
      setDraft(new Set());
      return;
    }
    setDraft((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const clearDraft = () => {
    setDraft(new Set());
    setDateChecked(true);
    // A diferencia de los nodos (que pasan por el borrador y solo se confirman al pulsar "Aplicar
    // filtros"), "Limpiar" borra la fecha ya mismo en vez de esperar, para dar una limpieza total
    // e inmediata de todos los filtros con un solo botón.
    onClearDate?.();
  };

  const draftIds = [...draft];
  const selectedLabels = draftIds
    .map((id) => {
      const node = sortedRoots.find((r) => r.id === id) || Object.values(childrenByParent).flat().find((c) => c.id === id);
      return node ? getTranslatedName(node) : null;
    })
    .filter(Boolean);

  // Resaltado de los botones: al abrir sin filtros, ninguno; al abrir con filtros ya aplicados,
  // "Limpiar"; en cuanto el borrador difiere de lo que había al abrir, "Aplicar filtros".
  // "Limpiar" borra la fecha al instante (onClearDate) y dateLabel desaparece, por eso la fecha
  // actual se compara con `hadDate` y no con el check.
  const initial = initialRef.current;
  const hasDateNow = !!(dateLabel && dateChecked);
  const draftChanged =
    hasDateNow !== initial.hadDate || draft.size !== initial.ids.size || draftIds.some((id) => !initial.ids.has(id));
  const hadFiltersOnOpen = initial.ids.size > 0 || initial.hadDate;
  const highlightApply = draftChanged;
  const highlightClear = !draftChanged && hadFiltersOnOpen;

  const applyLabel =
    draftIds.length > 0
      ? t("search.buscar.applyFiltersCount", { count: draftIds.length })
      : t("search.buscar.applyFilters");

  // El árbol de nodos puede tener más de un nivel (p.ej. La 8 -> La 8 Bierzo -> La 8 Bierzo -
  // Sanabria): la sangría tiene que crecer con la profundidad real (`depth`), no ser un simple
  // sí/no. Antes, un nodo intermedio con hijos propios (has_children en un hijo, no solo en la
  // raíz) se pintaba en la rama "padre" sin aplicar la sangría de "hijo" que traía, y sus propios
  // hijos recibían siempre la misma sangría fija de un solo nivel — de ahí que un nieto quedara
  // alineado con sus tíos en vez de un nivel más adentro que su padre.
  const INDENT_PX = 34;

  // "Seleccionar todos los hijos" (pedido del equipo): recorre el árbol bajo `node` recursivamente
  // (hijos, nietos, ...) y devuelve los ids de las hojas (los únicos nodos con checkbox propio; un
  // nodo padre no es un valor de filtro en sí mismo). Usa `childrenByParent` (el mapa ya cargado,
  // siempre al día porque es una prop) para las ramas que ya se conocen, y solo cae a
  // `loadChildren` para las que todavía no se han pedido nunca — evitar depender de la rama
  // "ya cargado" de `loadChildren` importa aquí: esa rama arrastra un closure obsoleto (memoizado
  // solo por `interfazId`) que devolvería `childrenByParent` vacío en vez del real.
  const collectDescendantIds = async (node) => {
    if (!node.has_children) return [node.id];
    const kids = childrenByParent[node.id] || (await loadChildren(node.id));
    const nested = await Promise.all(kids.map((child) => collectDescendantIds(child)));
    return nested.flat();
  };

  // Estado "marcado" del checkbox de un padre: solo se puede afirmar con datos ya cargados (sin
  // disparar peticiones desde el render); si alguna rama todavía no se ha pedido, se trata como no
  // marcado del todo (por defecto, hasta que el propio checkbox la cargue y seleccione).
  const isFullyChecked = (node) => {
    if (!node.has_children) return draft.has(node.id);
    const kids = childrenByParent[node.id];
    if (!kids || kids.length === 0) return false;
    return kids.every((child) => isFullyChecked(child));
  };

  const toggleSelectAllChildren = async (node) => {
    const ids = await collectDescendantIds(node);
    if (!ids.length) return;
    setDraft((prev) => {
      const next = new Set(prev);
      const allSelected = ids.every((id) => next.has(id));
      if (allSelected) {
        ids.forEach((id) => next.delete(id));
      } else {
        ids.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const renderRow = (node, depth = 0) => {
    const rowStyle = depth > 0 ? { paddingLeft: depth * INDENT_PX } : undefined;

    if (node.has_children) {
      const isExpanded = expanded.has(node.id);
      const allChecked = isExpanded && isFullyChecked(node);
      return (
        <div key={node.id}>
          <div
            className="bq-modal-row bq-modal-row--parent"
            style={rowStyle}
            role="button"
            tabIndex={0}
            onClick={() => toggleExpand(node.id)}
            onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && toggleExpand(node.id)}
          >
            <span className="bq-modal-row-check">
              {isExpanded && (
                <span
                  className="bq-modal-parent-check"
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                >
                  <input
                    type="checkbox"
                    checked={allChecked}
                    aria-label={t("search.buscar.selectAllChildren", { name: getTranslatedName(node) })}
                    onChange={() => toggleSelectAllChildren(node)}
                  />
                  <span className={`bq-checkbox${allChecked ? " is-checked" : ""}`} aria-hidden="true">
                    {allChecked && (
                      <svg width="14" height="14" viewBox="0 0 24 24">
                        <polyline points="4 12 10 18 20 6" />
                      </svg>
                    )}
                  </span>
                </span>
              )}
              <span>{getTranslatedName(node)}</span>
            </span>
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              className={`bq-icon bq-chevron${isExpanded ? " is-open" : ""}`}
              aria-hidden="true"
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </div>
          {isExpanded && (childrenByParent[node.id] || []).map((child) => renderRow(child, depth + 1))}
        </div>
      );
    }

    const checked = isAllNode(node.id) ? draft.size === 0 : draft.has(node.id);
    return (
      <label key={node.id} className="bq-modal-row" style={rowStyle}>
        <span className="bq-modal-row-check">
          <input type="checkbox" checked={checked} onChange={() => toggle(node.id)} />
          <span className={`bq-checkbox${checked ? " is-checked" : ""}`} aria-hidden="true">
            {checked && (
              <svg width="14" height="14" viewBox="0 0 24 24">
                <polyline points="4 12 10 18 20 6" />
              </svg>
            )}
          </span>
          <span className={checked ? "is-checked-label" : ""}>{getTranslatedName(node)}</span>
        </span>
      </label>
    );
  };

  // Portal a <body>: la barra superior es `position: fixed` con z-index propio y, renderizada
  // dentro de la página, la modal quedaba por debajo de ella (en móvil tapaba la cabecera con el
  // título y la X). Fuera de cualquier contexto de apilamiento de la página, el z-index del
  // overlay basta para quedar por encima.
  return ReactDOM.createPortal(
    <div className="bq-modal-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="bq-modal" role="dialog" aria-modal="true" aria-label={t("search.buscar.filters")} ref={panelRef} tabIndex={-1}>
        <div className="bq-modal-drag" aria-hidden="true" />
        <div className="bq-modal-header">
          <div>
            <h3>{t("search.buscar.filters")}</h3>
            <p>{t("search.buscar.modalIntro")}</p>
          </div>
          <button type="button" aria-label={t("global.button.close")} onClick={onClose} className="bq-modal-close">
            <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true">
              <line x1="5" y1="5" x2="19" y2="19" />
              <line x1="19" y1="5" x2="5" y2="19" />
            </svg>
          </button>
        </div>
        <div className="bq-modal-list">
          {/* La fecha se aplica fuera de este modal (DateFilter.js, sin borrador/Aplicar), pero
              aquí sí se puede desmarcar como el resto de filtros: se queda en el borrador local
              (`dateChecked`) hasta pulsar "Aplicar filtros", momento en que, si se desmarcó, se
              borra de verdad (onClearDate) igual que "Limpiar" (clearDraft) la borra al instante. */}
          {dateLabel && (
            <label className="bq-modal-row">
              <span className="bq-modal-row-check">
                <input type="checkbox" checked={dateChecked} onChange={() => setDateChecked((v) => !v)} />
                <span className={`bq-checkbox${dateChecked ? " is-checked" : ""}`} aria-hidden="true">
                  {dateChecked && (
                    <svg width="14" height="14" viewBox="0 0 24 24">
                      <polyline points="4 12 10 18 20 6" />
                    </svg>
                  )}
                </span>
                <span className={dateChecked ? "is-checked-label" : ""}>{dateLabel}</span>
              </span>
            </label>
          )}
          {sortedRoots.map((node) => renderRow(node))}
        </div>
        {selectedLabels.length >= 2 && (
          <div className="bq-modal-notice" role="status">
            <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="11" x2="12" y2="17" />
              <circle cx="12" cy="7.5" r="0.6" />
            </svg>
            <span>
              {t("search.buscar.selectedCount", { count: selectedLabels.length })} · {joinWithOr(selectedLabels, t("search.buscar.or"))}.
            </span>
          </div>
        )}
        <div className="bq-modal-actions">
          <button
            type="button"
            className={`bq-modal-apply${highlightApply ? " is-active" : ""}`}
            onClick={() => onApply(draftIds, { clearDate: !!(dateLabel && !dateChecked) })}
          >
            {applyLabel}
          </button>
          <button type="button" className={`bq-modal-clear${highlightClear ? " is-active" : ""}`} onClick={clearDraft}>
            {t("search.buscar.clearDraft")}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default FilterModal;
